import { type Component, For, type JSX, Show } from "solid-js";

import { Kbd, PulseDot, StatCell } from "./primitives";

export interface TopStripStat {
  label: string;
  value: JSX.Element;
  unit?: JSX.Element;
  delta?: JSX.Element;
  tone?: "ok" | "warn" | "muted";
}

export interface UserSummary {
  email: string;
  role?: string;
  onLogout?: () => void;
}

export const AppFrame: Component<{
  children: JSX.Element;
  accent?: "mint" | "blue" | "amber";
  omnibar?: JSX.Element;
}> = (props) => (
  <main class={`op-frame op-frame-${props.accent ?? "mint"}`}>
    <div class="op-frame-body">{props.children}</div>
    {props.omnibar}
  </main>
);

export const TopStrip: Component<{
  breadcrumb?: JSX.Element;
  stats?: TopStripStat[];
  user?: UserSummary | null;
  accent?: "mint" | "blue" | "amber";
  onBrandClick?: () => void;
}> = (props) => (
  <header class="op-top-strip">
    <button type="button" class="op-brand-cell" onClick={props.onBrandClick}>
      <PulseDot accent={props.accent ?? "mint"} />
      <b>z01.ai</b>
      <span>sfo-1</span>
    </button>
    <For each={props.stats ?? []}>{(stat) => <StatCell {...stat} />}</For>
    <div class="op-breadcrumb">{props.breadcrumb}</div>
    <div class="op-user-cell">
      <Show
        when={props.user}
        fallback={<span class="op-user-muted">signed out</span>}
      >
        {(user) => (
          <>
            <div class="op-user-copy">
              <b>{user().email}</b>
              <span>
                {user().role ?? "user"}
                <Show when={user().onLogout}>
                  {(logout) => (
                    <>
                      {" · "}
                      <button type="button" onClick={logout()}>
                        logout
                      </button>
                    </>
                  )}
                </Show>
              </span>
            </div>
            <div class="op-avatar">
              {user().email.slice(0, 1).toUpperCase()}
            </div>
          </>
        )}
      </Show>
    </div>
  </header>
);

export const Omnibar: Component<{
  children?: JSX.Element;
  hints?: Array<{ key: string; label: string }>;
  accent?: "mint" | "blue" | "amber";
  onActivate?: () => void;
}> = (props) => (
  <footer
    class={`op-omnibar op-omnibar-${props.accent ?? "mint"}${
      props.onActivate ? " op-omnibar-clickable" : ""
    }`}
    onClick={() => props.onActivate?.()}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        props.onActivate?.();
      }
    }}
    role={props.onActivate ? "button" : undefined}
    tabIndex={props.onActivate ? 0 : undefined}
    aria-label={props.onActivate ? "open command palette" : undefined}
  >
    <span class="op-omni-prompt">z01 ›</span>
    <span class="op-omni-input">
      {props.children ?? "type a command, paste a model id, or ask anything…"}
      <span class="op-caret" />
    </span>
    <div class="op-omni-hints">
      <For each={props.hints ?? DEFAULT_HINTS}>
        {(hint) => (
          <span>
            <Kbd>{hint.key}</Kbd>
            {hint.label}
          </span>
        )}
      </For>
    </div>
  </footer>
);

const DEFAULT_HINTS = [
  { key: "⌘K", label: "palette" },
  { key: "/", label: "search" },
  { key: "?", label: "shortcuts" },
];

export const AuthOverlay: Component<{
  on: boolean;
  direction: "in" | "out" | "nav";
}> = (props) => {
  const lines = () => {
    switch (props.direction) {
      case "in":
        return [
          ["$", "z01 auth login --browser", "opened"],
          ["→", "verifying workspace", "✓"],
          ["→", "loading dashboard · sfo-1", "✓"],
        ];
      case "out":
        return [
          ["$", "z01 auth logout", "ok"],
          ["→", "clearing session token", "✓"],
          ["→", "returning to homepage", "✓"],
        ];
      case "nav":
        return [
          ["$", "z01 nav --route", "ok"],
          ["→", "hydrating panel state", "✓"],
          ["→", "attaching live streams", "✓"],
        ];
    }
  };

  return (
    <div class={`op-auth-overlay ${props.on ? "op-auth-overlay-on" : ""}`}>
      <div class="op-auth-card">
        <For each={lines()}>
          {([prompt, command, ok]) => (
            <div class="op-auth-row">
              <span class="op-auth-prompt">{prompt}</span>
              <span class="op-auth-command">{command}</span>
              <span class="op-auth-ok">{ok}</span>
            </div>
          )}
        </For>
        <div class="op-auth-bar">
          <i />
        </div>
      </div>
    </div>
  );
};
